import { format } from "date-fns";
import { FC, ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { CheckCircleFilled, FileDownload, Tickets, WarningFilled } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, Loader, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { DateFormat } from "constants/date";
import { TicketsRoutes, getPathForRoute } from "constants/tickets/TicketsRoutes";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useChatWidget } from "hooks/use-chat-widget";
import { useTicketsToImport } from "hooks/useTicketsToImport";
import { TicketSilo } from "interfaces/entities/ticket";
import {
  selectLiveChatFullImportFailed,
  selectLiveChatFullImportInProgress,
  selectLiveChatFullImportNotPerformed,
  selectLiveChatFullImportStatusUpdateTime,
  selectLiveChatImportFinished,
  selectLiveChatSelectiveImportInProgress,
  selectLiveChatTicketsImportDateRange,
  selectLiveChatTicketsImportProgress,
  selectLiveChatTicketsImported,
} from "store/entities/integrations";
import { TicketsViewActions } from "store/views/tickets/actions";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import { LiveChatDataImportType } from "../../constants";

import { FullImportModal } from "./FullImportModal";
import { ImportItem } from "./ImportItem";

type FullImportStatus = "importing" | "completed" | "notPerformed" | "checking" | "failed";

const defaultTitle = "Tickets";

const pluralizeTickets = (count: number): string => plural(count, "ticket", "tickets");

export const FullImportItem: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { openChatWidget } = useChatWidget();

  const [displayFullImportModal, setDisplayFullImportModal] = useState(false);
  const { ticketsToImport } = useTicketsToImport();

  const fullImportInProgress = useSelector(selectLiveChatFullImportInProgress);
  const selectiveImportInProgress = useSelector(selectLiveChatSelectiveImportInProgress);
  const importStatusUpdatedAt = useSelector(selectLiveChatFullImportStatusUpdateTime);
  const importTicketsProgress = useSelector(selectLiveChatTicketsImportProgress);
  const importingTicketsFrom = useSelector(selectLiveChatTicketsImportDateRange);
  const importedTickets = useSelector(selectLiveChatTicketsImported);
  const importedArchivedTickets = importedTickets ? importedTickets.archive : 0;
  const importedRecentTickets = importedTickets ? importedTickets.recent : 0;
  const fullImportCompleted = useSelector(selectLiveChatImportFinished);
  const fullImportFailed = useSelector(selectLiveChatFullImportFailed);
  const fullImportNotPerformed = useSelector(selectLiveChatFullImportNotPerformed);

  const checkImportStatus = (): FullImportStatus => {
    if (fullImportInProgress) return "importing";
    if (fullImportCompleted) return "completed";
    if (fullImportNotPerformed) return "notPerformed";
    if (fullImportFailed) return "failed";

    return "checking";
  };

  const importStatus = checkImportStatus();

  const showImportModal = (): void => {
    trackEngagement("LC integration: Import modal opened", EventPlace.Settings);
    setDisplayFullImportModal(true);
  };

  const closeImportModal = (): void => {
    setDisplayFullImportModal(false);
  };

  const navigateToRecentTickets = (): void => {
    dispatch(
      TicketsViewActions.setView({
        route: TicketsRoutes.All,
        silo: TicketSilo.Tickets,
        filters: {},
      })
    );
    history.push(getPathForRoute(TicketsRoutes.All));
  };

  const navigateToArchivedTickets = (): void => {
    dispatch(
      TicketsViewActions.setView({
        route: TicketsRoutes.Archive,
        silo: TicketSilo.Archive,
        filters: {},
      })
    );
    history.push(getPathForRoute(TicketsRoutes.Archive));
  };

  const title: Record<FullImportStatus, string> = {
    checking: defaultTitle,
    notPerformed: defaultTitle,
    importing: defaultTitle,
    completed: `Tickets imported successfully on ${format(importStatusUpdatedAt || "", "MMM. D, YYYY, [at] h:mm A")}`,
    failed: `Tickets import failed. ${format(importStatusUpdatedAt || "", DateFormat.DateTime)}`,
  };

  const status: Record<FullImportStatus, ReactNode> = {
    checking: (
      <>
        <Loader size="small" />
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Checking for unimported tickets…</Text>
      </>
    ),
    notPerformed: ticketsToImport && (
      <>
        <Icon source={FileDownload} kind="action-warning" size="small" />
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
          Unimported: {ticketsToImport.count} {pluralizeTickets(ticketsToImport.count)}
        </Text>
      </>
    ),
    importing: importingTicketsFrom !== undefined && (
      <>
        <Loader size="small" />
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
          Imported <b>{importTicketsProgress} tickets</b>. Importing tickets from{" "}
          <b>{importingTicketsFrom === null ? "all time" : format(importingTicketsFrom, DateFormat.FullMonthYear)}</b>
          ...
        </Text>
      </>
    ),
    completed: (
      <>
        <Icon source={CheckCircleFilled} kind="success" size="small" />
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
          {importedRecentTickets} {pluralizeTickets(importedRecentTickets)} are in{" "}
          <Button kind="link-light" onClick={navigateToRecentTickets}>
            <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>recent tickets</Text>
          </Button>
          , {importedArchivedTickets} {pluralizeTickets(importedRecentTickets)} moved to{" "}
          <Button kind="link-light" onClick={navigateToArchivedTickets}>
            <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Archive</Text>
          </Button>
        </Text>
      </>
    ),
    failed: (
      <Flex gap="4px" alignItems="center">
        <Icon source={WarningFilled} kind="error" size="small" />
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
          <Button kind="link-light" onClick={openChatWidget}>
            <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Contact us</Text>
          </Button>{" "}
          for help
        </Text>
      </Flex>
    ),
  };

  if (!ticketsToImport?.hasTickets && importStatus === "notPerformed") {
    return null;
  }

  return (
    <>
      <ImportItem
        key={LiveChatDataImportType.Tickets}
        title={title[importStatus]}
        icon={Tickets}
        status={status[importStatus]}
        disabled={importStatus === "checking" || selectiveImportInProgress}
        disableReason={
          selectiveImportInProgress ? "Tickets cannot be imported until all other data has been imported" : undefined
        }
        loading={importStatus === "importing"}
        onImportClick={showImportModal}
        hideButton={importStatus === "completed" || importStatus === "failed"}
      />
      {displayFullImportModal && <FullImportModal onClose={closeImportModal} />}
    </>
  );
};
